import { useState } from 'react';
import { badgePhotoUrl, initials } from '../utils/identity';

/**
 * The signed-in user's badge photo for the top navigation.
 *
 * Rendered through TopNavigation's `iconSvg` slot rather than `iconUrl` because
 * the image needs a fallback and `iconUrl` gives none.
 *
 * Sizing and alignment live in `src/styles/avatar.css`, not here. Cloudscape
 * hard-sizes its icon slot to a 16px glyph box and forces `vertical-align: top`
 * on any child image, using selectors with ID-level specificity. Inline styles
 * cannot win that, and cannot resize the wrapper at all — so a 28px image
 * inline-styled here overflowed a 16px box into the alias text. The stylesheet
 * overrides both the wrapper and the image, scoped to the `ti-avatar` class.
 *
 * Do not "fix" the auth problem by pointing at
 * internal-cdn.amazon.com/badgephotos.amazon.com — it answers without auth but
 * returns a byte-identical placeholder silhouette for every alias, which looks
 * like it works while telling the user nothing.
 */
export function UserAvatar({ alias }: { alias: string }) {
  const [failed, setFailed] = useState(false);

  // The adjacent utility text already names the user, so the image is decorative
  // and must not be announced twice.
  if (failed) {
    return (
      <span className="ti-avatar ti-avatar--initials" aria-hidden="true">
        {initials(alias)}
      </span>
    );
  }

  return (
    <img
      className="ti-avatar"
      src={badgePhotoUrl(alias)}
      alt=""
      aria-hidden="true"
      onError={() => setFailed(true)}
    />
  );
}
